/**
 * Catch-All Slug Client Component — App Router
 *
 * Renders dynamic pages (category, product, CMS, brand) based on URL.
 * Server Component resolves the URL type and passes data here.
 *
 * Performance: Page wrappers use dynamic() imports so only the matched
 * page type's JS bundle is loaded. Combined with SSR, the HTML is still
 * rendered server-side but JS code-splitting reduces First Load JS significantly.
 *
 * GAP-2: Added ViewContentTracking for product detail pages.
 * GAP-3: Added Brand page support as fallback.
 */
'use client';

import React, {useMemo} from 'react';
import dynamic from 'next/dynamic';
import {PageType} from '@/_application/core/types';
import type {DynamicPageServerSideProps} from '@/_application/core/types';
import type {ViewContentCommandInput} from '@kidslabs/mario-client-event-tracking';
import {useClientIsMobile} from '@/lib/hooks/use-client-is-mobile';

// Dynamic imports — only the matched page type's JS is loaded (SSR still renders HTML)
const CategoryPageWrapper = dynamic(
    () => import('@/_application/pages/category/_isr/category-page-wrapper').then(m => m.CategoryPageWrapper),
    {ssr: true},
);
const ProductDetailPageWrapper = dynamic(
    () => import('@/_application/pages/product-detail/_isr/product-detail-page-wrapper').then(m => m.ProductDetailPageWrapper),
    {ssr: true},
);
const CmsPageWrapper = dynamic(
    () => import('@/_application/pages/cms/_isr/cms-page-wrapper').then(m => m.CmsPageWrapper),
    {ssr: true},
);
const BrandPageWrapper = dynamic(
    () => import('@/_application/pages/brand/_isr/brand-page-wrapper').then(m => m.BrandPageWrapper),
    {ssr: true},
);

// Tracking — keep as dynamic/ssr:false since it's non-visual
const ViewContentTracking = dynamic(
    () => import('@/_application/modules/tracking/components/view-content-tracking').then(m => m.ViewContentTracking),
    {ssr: false},
);

interface CatchAllPageClientProps {
    pageType: string;
    variables: DynamicPageServerSideProps['variables'] | null;
    isMobile?: boolean;
}

/**
 * Build ViewContent tracking input from product data.
 * Matches the ISR [[...pages]].tsx tracking logic.
 */
function useProductTrackingInput(
    pageType: string,
    variables: DynamicPageServerSideProps['variables'] | null,
): ViewContentCommandInput | null {
    return useMemo(() => {
        if (pageType !== PageType.PRODUCT_DETAIL || !variables?.productDetail?.product) {
            return null;
        }

        const product = variables.productDetail.product;

        // Dynamically imported utils are not available here at module scope,
        // so we inline the essential tracking data extraction.
        const trackingInput: ViewContentCommandInput = {
            viewType: 'ViewContent',
            content_name: product?.name ?? 'no-product-name',
        };

        // Extract price from product price range
        const priceRange = product?.price_range;
        const finalPrice = priceRange?.minimum_price?.final_price;
        if (finalPrice) {
            trackingInput.value = finalPrice.value ?? 0;
            trackingInput.currency = finalPrice.currency ?? 'VND';
        }

        // Build content entry
        trackingInput.contents = [
            {
                id: product?.sku ?? product?.id?.toString() ?? 'no-product-id',
                quantity: 1,
            },
        ];

        return trackingInput;
    }, [pageType, variables]);
}

export function CatchAllPageClient({pageType, variables, isMobile: isMobileFromServer}: CatchAllPageClientProps) {
    const trackingInput = useProductTrackingInput(pageType, variables);
    const isMobileFromClient = useClientIsMobile();
    const isMobile = isMobileFromServer ?? isMobileFromClient;

    if (!variables) {
        return null;
    }

    return (
        <>
            {/* GAP-2: ViewContentTracking for product detail pages */}
            {trackingInput?.contents && <ViewContentTracking page={pageType as PageType} input={trackingInput} />}

            {(() => {
                switch (pageType) {
                    case PageType.CATEGORY:
                        return <CategoryPageWrapper variables={variables} isMobile={isMobile} />;
                    case PageType.PRODUCT_DETAIL:
                        return <ProductDetailPageWrapper variables={variables} isMobile={isMobile} />;
                    case PageType.BRAND:
                        return <BrandPageWrapper variables={variables} isMobile={isMobile} />;
                    case PageType.CMS:
                    default:
                        return <CmsPageWrapper variables={variables} isMobile={isMobile} />;
                }
            })()}
        </>
    );
}
